import { apiSlice } from './apiSlice';

// Every id in this file is typed `number | string`, so it is URL-encoded before
// interpolation: a value carrying '/', '?', '#' or '../' would otherwise
// rewrite the request path (path traversal / route tampering).
// encodeURIComponent is a no-op for numeric ids.
const segment = (id: number | string): string => encodeURIComponent(String(id));

export const fixedAssetsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Assets
    getAssets: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/fixed-assets',
        params: params || {},
      }),
      providesTags: ['FixedAsset'],
    }),
    getAllAssets: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/fixed-assets',
        params: params || {},
      }),
      providesTags: ['FixedAsset'],
    }),
    getAssetById: builder.query<any, number | string>({
      query: (id) => ({ url: `/fixed-assets/${segment(id)}` }),
      providesTags: (result, error, id) => [{ type: 'FixedAsset', id }],
    }),
    createAsset: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fixed-assets',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['FixedAsset'],
    }),
    updateAsset: builder.mutation<any, { id: number | string; data: any }>({
      query: ({ id, data }) => ({
        url: `/fixed-assets/${segment(id)}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'FixedAsset', id }, 'FixedAsset'],
    }),
    deleteAsset: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/fixed-assets/${segment(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FixedAsset'],
    }),
    deleteMultipleAssets: builder.mutation<any, (number | string)[]>({
      query: (ids) => ({
        url: '/fixed-assets/delete/by_selection',
        method: 'POST',
        data: { ids },
      }),
      invalidatesTags: ['FixedAsset'],
    }),
    revaluateAsset: builder.mutation<any, { id?: number | string; asset_id?: number | string; data?: any; [key: string]: any }>({
      query: (params) => {
        const id = params.id || params.asset_id || '';
        const body = params.data || params;
        return {
          url: `/fixed-assets/${segment(id)}/revaluate`,
          method: 'POST',
          // axiosBaseQuery only forwards `data` — `body` is silently dropped.
          data: body,
        };
      },
      invalidatesTags: (result, error, { id, asset_id }) => [{ type: 'FixedAsset', id: id || asset_id }, 'FixedAsset'],
    }),

    // Categories
    getFixedAssetCategories: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/fixed-assets/categories',
        params: params || {},
      }),
      providesTags: ['AssetCategory'],
    }),
    createFixedAssetCategory: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fixed-assets/categories',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['AssetCategory'],
    }),
    updateFixedAssetCategory: builder.mutation<any, { id: number | string; data: any }>({
      query: ({ id, data }) => ({
        url: `/fixed-assets/categories/${segment(id)}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AssetCategory', id }, 'AssetCategory'],
    }),
    deleteFixedAssetCategory: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/fixed-assets/categories/${segment(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AssetCategory'],
    }),

    // Locations
    getLocations: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/fixed-assets/locations',
        params: params || {},
      }),
      providesTags: ['AssetLocation'],
    }),
    createLocation: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fixed-assets/locations',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['AssetLocation'],
    }),
    updateLocation: builder.mutation<any, { id: number | string; data: any }>({
      query: ({ id, data }) => ({
        url: `/fixed-assets/locations/${segment(id)}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AssetLocation', id }, 'AssetLocation'],
    }),
    deleteLocation: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/fixed-assets/locations/${segment(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AssetLocation'],
    }),

    // Depreciation
    getDepreciationReport: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/fixed-assets/report/depreciation',
        params: params || {},
      }),
      providesTags: ['FixedAsset'],
    }),

    // Depreciation Schedules
    // NOTE: these used to be written as '/fixed-assets/../depreciation-schedules…'
    // and relied on the browser collapsing the '..' segment before sending the
    // request. Any client that does not normalise (or a proxy that rejects dot
    // segments) hit the wrong route — use the real path.
    getDepreciationSchedules: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/depreciation-schedules',
        params: params || {},
      }),
      providesTags: ['DepreciationSchedule'],
    }),
    createDepreciationSchedule: builder.mutation<any, any>({
      query: (data) => ({
        url: '/depreciation-schedules',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['DepreciationSchedule'],
    }),
    updateDepreciationSchedule: builder.mutation<any, { id: number | string; data: any }>({
      query: ({ id, data }) => ({
        url: `/depreciation-schedules/${segment(id)}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'DepreciationSchedule', id }, 'DepreciationSchedule'],
    }),
    deleteDepreciationSchedule: builder.mutation<void, number | string>({
      query: (id) => ({
        url: `/depreciation-schedules/${segment(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DepreciationSchedule'],
    }),
    activateDepreciationSchedule: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/depreciation-schedules/${segment(id)}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'DepreciationSchedule', id }, 'DepreciationSchedule'],
    }),
    deactivateDepreciationSchedule: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/depreciation-schedules/${segment(id)}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'DepreciationSchedule', id }, 'DepreciationSchedule'],
    }),
    runDepreciationSchedule: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/depreciation-schedules/${segment(id)}/run`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'DepreciationSchedule', id }, 'DepreciationSchedule', 'FixedAsset'],
    }),
    previewDepreciationScheduleRun: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/depreciation-schedules/${segment(id)}/preview`,
        method: 'POST',
      }),
    }),
    getDepreciationScheduleHistory: builder.query<any, { id: number | string; params?: Record<string, any> }>({
      query: ({ id, params }) => ({
        url: `/depreciation-schedules/${segment(id)}/history`,
        params: params || {},
      }),
      providesTags: (result, error, { id }) => [{ type: 'DepreciationSchedule', id }],
    }),
  }),
});

export const {
  useGetAssetsQuery,
  useGetAllAssetsQuery,
  useGetAssetByIdQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useDeleteMultipleAssetsMutation,
  useRevaluateAssetMutation,
  useGetFixedAssetCategoriesQuery: useGetCategoriesQuery,
  useCreateFixedAssetCategoryMutation: useCreateCategoryMutation,
  useUpdateFixedAssetCategoryMutation: useUpdateCategoryMutation,
  useDeleteFixedAssetCategoryMutation: useDeleteCategoryMutation,
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useGetDepreciationReportQuery,
  useGetDepreciationSchedulesQuery,
  useCreateDepreciationScheduleMutation,
  useUpdateDepreciationScheduleMutation,
  useDeleteDepreciationScheduleMutation,
  useActivateDepreciationScheduleMutation,
  useDeactivateDepreciationScheduleMutation,
  useRunDepreciationScheduleMutation,
  usePreviewDepreciationScheduleRunMutation,
  useGetDepreciationScheduleHistoryQuery,
} = fixedAssetsApi;
